import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Editor from '@monaco-editor/react';
import CronBuilder from '../components/CronBuilder';
import {
  Save,
  X,
  Play,
  AlertCircle,
  Clock,
  Zap,
  Code,
  Settings
} from 'lucide-react';

interface JobForm {
  name: string;
  description: string;
  script: string;
  schedule: string;
  type: 'cron' | 'manual';
  enabled: boolean;
  timeout_seconds: number;
  max_retries: number;
  env_vars: Record<string, string>;
}

const DEFAULT_SCRIPT = `// Your JavaScript code here
// Available globals: console, fetch, env

console.log('Job started at:', new Date().toISOString());

// Example: Fetch data from API
const response = await fetch('https://api.example.com/data');
const data = await response.json();

console.log('Fetched data:', data);

// Return value will be saved in execution history
return { success: true, recordsProcessed: data.length };
`;

const JOB_TEMPLATES = [
  {
    name: 'Database Backup',
    script: `// Database backup job
console.log('Starting database backup...');

const backupName = \`backup_\${new Date().toISOString().split('T')[0]}\`;

// Execute backup command
// Note: This is a placeholder - implement actual backup logic
console.log('Backup name:', backupName);

return { success: true, backupName };`,
  },
  {
    name: 'Data Sync',
    script: `// Data synchronization job
console.log('Starting data sync...');

const sourceAPI = env.SOURCE_API_URL;
const targetAPI = env.TARGET_API_URL;

// Fetch from source
const response = await fetch(sourceAPI);
const data = await response.json();

// Push to target
await fetch(targetAPI, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(data)
});

return { success: true, recordsSynced: data.length };`,
  },
  {
    name: 'Health Check',
    script: `// Service health check
const services = [
  'https://api.weretrade.com/health',
  'https://login.weretrade.com/health'
];

const results = [];

for (const service of services) {
  try {
    const response = await fetch(service, { timeout: 5000 });
    results.push({
      service,
      status: response.ok ? 'healthy' : 'unhealthy',
      responseTime: response.headers.get('x-response-time')
    });
  } catch (error) {
    results.push({ service, status: 'error', error: error.message });
  }
}

return { results };`,
  },
];

export default function JobEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [form, setForm] = useState<JobForm>({
    name: '',
    description: '',
    script: DEFAULT_SCRIPT,
    schedule: '0 0 * * *', // Daily at midnight
    type: 'cron',
    enabled: true,
    timeout_seconds: 300,
    max_retries: 3,
    env_vars: {},
  });

  const [envVarInput, setEnvVarInput] = useState({ key: '', value: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showCronBuilder, setShowCronBuilder] = useState(false);

  useEffect(() => {
    if (isEditMode) {
      fetchJob();
    }
  }, [id]);

  const fetchJob = async () => {
    try {
      const response = await axios.get(`/api/jobs/${id}`);
      setForm(response.data);
    } catch (err) {
      setError('Failed to load job');
      console.error(err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');

    try {
      if (isEditMode) {
        await axios.put(`/api/jobs/${id}`, form);
      } else {
        await axios.post('/api/jobs', form);
      }
      navigate('/jobs');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save job');
    } finally {
      setIsSaving(false);
    }
  };

  const loadTemplate = (template: typeof JOB_TEMPLATES[0]) => {
    setForm({ ...form, name: template.name, script: template.script });
  };

  const addEnvVar = () => {
    if (envVarInput.key && envVarInput.value) {
      setForm({
        ...form,
        env_vars: { ...form.env_vars, [envVarInput.key]: envVarInput.value },
      });
      setEnvVarInput({ key: '', value: '' });
    }
  };

  const removeEnvVar = (key: string) => {
    const newEnvVars = { ...form.env_vars };
    delete newEnvVars[key];
    setForm({ ...form, env_vars: newEnvVars });
  };

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {isEditMode ? 'Edit Job' : 'Create New Job'}
          </h1>
          <p className="text-slate-400">
            {isEditMode ? 'Update job configuration' : 'Configure your cronjob or manual task'}
          </p>
        </div>
        <button
          onClick={() => navigate('/jobs')}
          className="flex items-center gap-2 px-4 py-2 text-slate-400 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
          <span>Cancel</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-200">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
          <h2 className="text-xl font-semibold text-white mb-4">Basic Information</h2>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Job Name *
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Job Type *
              </label>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value as 'cron' | 'manual' })}
                className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="cron">Cron (Scheduled)</option>
                <option value="manual">Manual (On-Demand)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Status
              </label>
              <label className="flex items-center gap-3 px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500"
                />
                <span className="text-white">Enabled</span>
              </label>
            </div>
          </div>
        </div>

        {/* Schedule */}
        {form.type === 'cron' && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white">Schedule</h2>
              <button
                type="button"
                onClick={() => setShowCronBuilder(!showCronBuilder)}
                className="flex items-center gap-2 px-3 py-1 text-sm text-blue-400 hover:bg-blue-900/50 rounded transition-colors"
              >
                <Clock className="w-4 h-4" />
                {showCronBuilder ? 'Hide' : 'Show'} Builder
              </button>
            </div>

            {showCronBuilder && (
              <CronBuilder
                value={form.schedule}
                onChange={(schedule) => setForm({ ...form, schedule })}
              />
            )}

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Cron Expression *
              </label>
              <input
                type="text"
                value={form.schedule}
                onChange={(e) => setForm({ ...form, schedule: e.target.value })}
                className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0 0 * * *"
                required
              />
              <p className="mt-2 text-xs text-slate-400">
                Format: minute hour day month weekday (e.g., "0 0 * * *" = daily at midnight)
              </p>
            </div>
          </div>
        )}

        {/* Script Editor */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white">Script</h2>
            <div className="flex gap-2">
              {JOB_TEMPLATES.map((template) => (
                <button
                  key={template.name}
                  type="button"
                  onClick={() => loadTemplate(template)}
                  className="px-3 py-1 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors"
                >
                  {template.name}
                </button>
              ))}
            </div>
          </div>

          <div className="border border-slate-600 rounded-lg overflow-hidden">
            <Editor
              height="400px"
              defaultLanguage="javascript"
              value={form.script}
              onChange={(value) => setForm({ ...form, script: value || '' })}
              theme="vs-dark"
              options={{
                minimap: { enabled: false },
                fontSize: 14,
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                automaticLayout: true,
              }}
            />
          </div>
        </div>

        {/* Environment Variables */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
          <h2 className="text-xl font-semibold text-white">Environment Variables</h2>

          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Key (e.g., API_KEY)"
              value={envVarInput.key}
              onChange={(e) => setEnvVarInput({ ...envVarInput, key: e.target.value })}
              className="flex-1 px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="text"
              placeholder="Value"
              value={envVarInput.value}
              onChange={(e) => setEnvVarInput({ ...envVarInput, value: e.target.value })}
              className="flex-1 px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={addEnvVar}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Add
            </button>
          </div>

          {Object.keys(form.env_vars).length > 0 && (
            <div className="space-y-2">
              {Object.entries(form.env_vars).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between p-3 bg-slate-900 rounded-lg">
                  <div className="flex items-center gap-4">
                    <code className="text-sm text-blue-400">{key}</code>
                    <span className="text-slate-500">=</span>
                    <code className="text-sm text-slate-300">{value}</code>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeEnvVar(key)}
                    className="text-red-400 hover:text-red-300 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Advanced Settings */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
          <h2 className="text-xl font-semibold text-white">Advanced Settings</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Timeout (seconds)
              </label>
              <input
                type="number"
                value={form.timeout_seconds}
                onChange={(e) => setForm({ ...form, timeout_seconds: parseInt(e.target.value) })}
                className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="10"
                max="3600"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Max Retries
              </label>
              <input
                type="number"
                value={form.max_retries}
                onChange={(e) => setForm({ ...form, max_retries: parseInt(e.target.value) })}
                className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                max="10"
              />
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex items-center justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/jobs')}
            className="px-6 py-2 text-slate-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            <Save className="w-4 h-4" />
            <span>{isSaving ? 'Saving...' : isEditMode ? 'Update Job' : 'Create Job'}</span>
          </button>
        </div>
      </form>
    </div>
  );
}
